/**
 * The Deal Mechanic panel — what to actually DO about the deal (module 6, workbook `9_DealMechanic`).
 *
 * ## Why this panel did not exist for months
 *
 * The module has been computed on **every** run since it shipped — public `/analyze` and the premium
 * authed path alike — and persisted into every `analyses.result`. Nothing read it. A sweep on
 * 2026-09-02 found its only reference anywhere in `packages/web` was a code comment, and its only
 * non-comment reference in the whole repo was a *label in a list* naming the module, not consuming its
 * result. So the report graded a deal and never said what would change it, while the answer sat in the
 * record. It was not withheld by a ruling and it is not entitlement-gated (`OPERATOR_ONLY_
 * DETERMINISTIC_KEYS` is `['rentSplit']` alone) — it was an omission. See [[built-but-nothing-reads-it]].
 *
 * ## The numbers are the engine's, all of them
 *
 * The workbook's hard rule (v2.6.4) is that JS owns the bps and model-stated numbers are overridden.
 * Every figure here is deterministic — this component formats, ranks nothing, and computes nothing.
 *
 * ## ⚠️ Two things this panel must never do
 *
 * 1. **Show only the quantified levers.** `status` carries four states and three of them are reasons.
 *    `no_delta` (tested, nothing to capture), `verify` (a validation override fired — e.g. a rent push
 *    clamped at the class band ceiling) and `insufficient_data` (the inputs to test it were missing)
 *    are different facts, and collapsing them into an absent row would let "already optimal" and "we
 *    could not look" render identically. That is the defect `rentPositionUnavailable` exists to stop
 *    one panel down, and naming a check without its report is a claim, not a gap (#492).
 * 2. **Render an empty panel when no class resolved.** `dealMechanic` is null exactly when
 *    `classCalibration` is, and `classUnavailable` says why. A blank lever list would read as "this
 *    deal has no levers" — a finding — when the truth is that nothing stated a class to calibrate
 *    against. The `NoClass` branch below carries that, with `absent` and `unmatched` distinguished
 *    because only the second is a reason to go back to the document.
 */

import type { ClassSignalState, DealMechanicResult, LeverImpact } from '../api.js';
import { basisPoints, currency, percentFromRatio } from '../format.js';

/** Human labels for the engine's `LeverType`. Unknown values fall through to the engine's own label. */
const LEVER_TYPE_LABELS: Record<string, string> = {
  rent_push: 'Rent',
  opex_reduction: 'Operating expense',
  ancillary_revenue: 'Ancillary income',
  debt_resize: 'Debt',
};

/** What each non-quantified status MEANS to a reader, in the reader's words rather than the enum's. */
const STATUS_EXPLANATION: Record<string, string> = {
  no_delta: 'Tested — nothing left to capture',
  verify: 'Needs verification — a validation limit applied',
  insufficient_data: 'Not testable — inputs missing',
  // The inputs were there; the engine declined to size the lever on them. The note says why.
  withheld: 'Withheld — not sized on these inputs',
};

/** Which signals were consulted for a class, phrased for a reader who has to go fix the package. */
const SIGNAL_LABELS: Record<string, string> = {
  propertyClass: 'Property class',
  reviewerContext: 'Reviewer notes',
  assetClass: 'Asset class',
  rentPSF: 'Rent per SF',
};

/**
 * A lever's current → target, in the lever's own units.
 *
 * ⚠️ The units differ BY LEVER TYPE — rent is $/unit/mo, opex is a ratio, debt is a rate — so a single
 * formatter across all of them would print an opex ratio of 0.35 as "$0" and a rate as "$0". Dispatch
 * on the type; an unrecognised type prints the raw values rather than a confidently wrong unit.
 */
function inLeverUnits(leverType: LeverImpact['leverType'], v: number): string {
  // `debt_resize` current/target pass through the engine's `asRatio` (`n > 1 ? n / 100 : n`), so they
  // ARE ratios by the time they reach here — `percentFromRatio` is correct, not a double-scale.
  if (leverType === 'opex_reduction' || leverType === 'debt_resize') return percentFromRatio(v);
  if (leverType === 'rent_push' || leverType === 'ancillary_revenue') {
    return `${currency(v)}/unit/mo`;
  }
  return String(v);
}

function movement(lever: LeverImpact): string | null {
  const { current, target, leverType } = lever;
  if (current === null || current === undefined || target === null || target === undefined) {
    return null;
  }
  if (!Number.isFinite(current) || !Number.isFinite(target)) return null;
  return `${inLeverUnits(leverType, current)} → ${inLeverUnits(leverType, target)}`;
}

/**
 * Where a NON-quantified lever currently stands — no arrow, no target.
 *
 * The target on these rows is a band boundary the engine tested against, not somewhere to go. Showing
 * the current value keeps the row informative ("your opex ratio is 32%") without prescribing a
 * direction the engine never recommended.
 */
function standing(lever: LeverImpact): string | null {
  const { current, leverType } = lever;
  if (current === null || current === undefined || !Number.isFinite(current)) return null;
  return inLeverUnits(leverType, current);
}

/**
 * One lever row. Quantified rows carry bps; the rest carry the reason they do not.
 *
 * 🔴 THE ARROW IS ONLY EVER SHOWN ON A QUANTIFIED LEVER, and that is a correctness rule, not a
 * styling choice. On the other three statuses the engine's `target` is a BAND BOUNDARY, not a
 * recommendation — an opex lever inside its band reports `current 0.32 → target 0.35` (the ceiling),
 * and a rent lever whose push was reversed reports a target BELOW current. Printing either under a
 * column headed "Move" instructs a developer to raise their opex ratio and cut their rents, on
 * exactly the rows where the engine concluded *do nothing*. The status badge sits two columns away
 * and does not neutralise a directional arrow.
 *
 * ⚠️ The first version of this component shipped that defect and the test fixture hid it: it used
 * `current: 0.35, target: 0.35`, the single value at which the arrow is invisible. A fixture that
 * cannot fail. Non-quantified rows now show where the value STANDS, with no arrow and no target.
 */
function LeverRow({ lever, ranked }: { lever: LeverImpact; ranked: boolean }) {
  const label = lever.label || LEVER_TYPE_LABELS[lever.leverType] || lever.leverType;
  const quantified = lever.status === 'quantified';
  const move = quantified ? movement(lever) : standing(lever);
  return (
    <tr className={ranked ? 'lever lever--ranked' : 'lever'}>
      <th scope="row" className="lever__label">
        {label}
        <span className="lever__type">{LEVER_TYPE_LABELS[lever.leverType] ?? lever.leverType}</span>
      </th>
      <td className="lever__move">{move ?? '—'}</td>
      <td className="lever__bps">{quantified ? basisPoints(lever.yocBps) : '—'}</td>
      <td className="lever__bps">{quantified ? basisPoints(lever.irrBps) : '—'}</td>
      <td className="lever__noi">
        {quantified && lever.noiAdd !== null && lever.noiAdd !== undefined
          ? currency(lever.noiAdd)
          : '—'}
      </td>
      <td className="lever__status">
        {quantified ? (
          <span className="lever__badge lever__badge--quantified">Quantified</span>
        ) : (
          <span className="lever__badge">{STATUS_EXPLANATION[lever.status] ?? lever.status}</span>
        )}
        {lever.note ? <span className="lever__note">{lever.note}</span> : null}
      </td>
    </tr>
  );
}

/**
 * "top lever" / "top two levers" — never "the top three levers".
 *
 * ⚠️ The engine caps the ranked set at three, but the READER PATH evaluates only two (the
 * deterministic bundle supplies no extra levers), so a hardcoded "top three" was false on every run
 * that reaches a client. The table already said `top {n}`; the headline did not, and the two
 * disagreed on the same page.
 */
function leverWord(n: number): string {
  if (n === 1) return 'top lever';
  if (n === 2) return 'top two levers';
  return `top ${n} levers`;
}

/**
 * The headline: how far the deal is from its target IRR, and whether the top-3 levers close it.
 *
 * ⚠️ `gapBpsToTarget` is POSITIVE when the deal is SHORT of the hurdle. A reader who assumes the usual
 * "negative = bad" convention reads the sign backwards, so the sign is never shown raw — it is turned
 * into words here ("short of" / "above") and the magnitude shown unsigned.
 */
function Gap({ mechanic, rankedCount }: { mechanic: DealMechanicResult; rankedCount: number }) {
  const gap = mechanic.gapBpsToTarget;
  const target = mechanic.targetIRR;
  const current = mechanic.currentIRR;
  const haveGap = gap !== null && gap !== undefined && Number.isFinite(gap);

  return (
    <div className="mechanic__gap">
      <div className="mechanic__gapfigure">
        <span className="mechanic__gaplabel">Project IRR today</span>
        <strong>
          {current !== null && current !== undefined ? percentFromRatio(current) : '—'}
        </strong>
      </div>
      <div className="mechanic__gapfigure">
        <span className="mechanic__gaplabel">Target</span>
        <strong>{target !== null && target !== undefined ? percentFromRatio(target) : '—'}</strong>
      </div>
      <div className="mechanic__gapfigure">
        <span className="mechanic__gaplabel">Gap</span>
        <strong>
          {haveGap
            ? gap > 0
              ? `${basisPoints(Math.abs(gap))} short`
              : gap < 0
                ? `${basisPoints(Math.abs(gap))} above`
                : 'at target'
            : '—'}
        </strong>
      </div>
      {haveGap && gap > 0 ? (
        <p className="mechanic__verdict">
          {rankedCount === 0 ? (
            /*
             * 🔴 NOT "the levers stack to 0 bps". `stackedIrrBps` is a reduce over an EMPTY set, so
             * it is 0 by arithmetic, not by measurement — and "we summed the levers and got nothing"
             * is a different claim from "we could not quantify any lever". This is reachable on an
             * ordinary run: the reader path supplies no extra levers, so only rent and opex are
             * evaluated, and rent goes `insufficient_data` without market comps while opex goes
             * `no_delta` inside its band. Both non-quantified is common, not a corner case.
             */
            <>No lever could be quantified against this gap — see the statuses below.</>
          ) : mechanic.closesGap === true ? (
            <>
              The {leverWord(rankedCount)} stack to{' '}
              <strong>{basisPoints(mechanic.stackedIrrBps)} of IRR</strong> — enough to close the
              gap.
            </>
          ) : (
            <>
              The {leverWord(rankedCount)} stack to{' '}
              <strong>{basisPoints(mechanic.stackedIrrBps)} of IRR</strong>, which does not close
              the gap on its own.
            </>
          )}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The panel shown when no canonical class resolved — a STATED absence, never a blank.
 *
 * The signals are listed in the order the engine consults them, with `unmatched` called out
 * separately: a class that was stated but unreadable is an actionable document problem, and an
 * `absent` one simply means nobody said.
 */
function NoClass({ signals }: { signals: ClassSignalState[] }) {
  const unmatched = signals.filter((s) => s.state === 'unmatched');
  return (
    <section className="card mechanic mechanic--noclass" aria-labelledby="mechanic-heading">
      <h3 className="card__title" id="mechanic-heading">
        Value-creation levers — not calculated
      </h3>
      <p className="card__prose">
        The levers are calibrated against a canonical property class, and none resolved for this
        deal. This is <strong>not</strong> a finding that the deal has no levers — it means there
        was nothing to calibrate against.
      </p>
      {signals.length > 0 ? (
        <ul className="mechanic__signals">
          {signals.map((s) => (
            <li key={s.signal}>
              <span className="mechanic__signal">{SIGNAL_LABELS[s.signal] ?? s.signal}</span>{' '}
              {s.state === 'unmatched' ? (
                <>
                  stated <q>{s.stated}</q>, which carries no class
                </>
              ) : (
                'not stated'
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {unmatched.length > 0 ? (
        <p className="mechanic__hint">
          Something was stated but could not be read as a class letter — worth checking the source
          document, unlike a value that was simply never given.
        </p>
      ) : null}
    </section>
  );
}

export interface DealMechanicCardProps {
  mechanic: DealMechanicResult | null | undefined;
  /** Why no class resolved — used only when `mechanic` is absent. */
  classUnavailable?: ClassSignalState[] | null;
}

/**
 * Render the Deal Mechanic, or state why it could not be computed.
 *
 * Returns null ONLY when there is nothing truthful to say — no mechanic and no explanation, which is
 * a run predating both fields. Every other path renders something, because an absent panel on a
 * report that has one for every other module reads as a finding.
 */
export function DealMechanicCard({ mechanic, classUnavailable }: DealMechanicCardProps) {
  if (!mechanic) {
    if (classUnavailable && classUnavailable.length > 0) {
      return <NoClass signals={classUnavailable} />;
    }
    return null;
  }

  const levers = mechanic.levers ?? [];
  const rankedIds = new Set((mechanic.rankedTop3 ?? []).map((l) => `${l.leverType}:${l.label}`));
  // Ranked levers first, in the engine's rank order, then the remainder in evaluation order. The
  // engine owns BOTH orderings; this only concatenates them.
  const ranked = mechanic.rankedTop3 ?? [];
  const rest = levers.filter((l) => !rankedIds.has(`${l.leverType}:${l.label}`));

  if (ranked.length === 0 && rest.length === 0) return null;

  return (
    <section className="card mechanic" aria-labelledby="mechanic-heading">
      <h3 className="card__title" id="mechanic-heading">
        Value-creation levers
      </h3>
      <p className="card__prose">
        Where the scorecard grades the deal, this ranks what would move it — each lever&rsquo;s
        impact on yield-on-cost and project IRR, against the gap to target. Every figure is
        engine-computed.
      </p>

      <Gap mechanic={mechanic} rankedCount={ranked.length} />

      {/*
        The overflow wrapper is REQUIRED, not decorative. The numeric cells are `white-space: nowrap`
        and the status column has a 160px floor — copied from the CSI panel, which pairs them with
        `.csi__tablewrap`. Copying the cell styles without the wrapper is what made the whole Deal
        Room scroll sideways on a phone: a quantified rent move is a long nowrap string, and a table
        that cannot shrink pushes the page instead. R9 makes this a defect rather than a polish item —
        a superintendent opens this on a phone.
      */}
      <div className="mechanic__tablewrap">
        <table className="mechanic__table">
          <thead>
            <tr>
              <th scope="col">Lever</th>
              <th scope="col">Move</th>
              <th scope="col">YOC</th>
              <th scope="col">IRR</th>
              <th scope="col">NOI added</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          {ranked.length > 0 ? (
            <tbody>
              <tr className="mechanic__grouprow">
                <th scope="colgroup" colSpan={6}>
                  Move the needle — top {ranked.length} by IRR impact
                </th>
              </tr>
              {ranked.map((l) => (
                <LeverRow key={`ranked-${l.leverType}-${l.label}`} lever={l} ranked />
              ))}
              {ranked.length > 1 ? (
                <tr className="mechanic__stacked">
                  <th scope="row">Stacked</th>
                  <td>—</td>
                  <td>{basisPoints(mechanic.stackedYocBps)}</td>
                  <td>{basisPoints(mechanic.stackedIrrBps)}</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              ) : null}
            </tbody>
          ) : null}
          {rest.length > 0 ? (
            <tbody>
              <tr className="mechanic__grouprow">
                <th scope="colgroup" colSpan={6}>
                  {ranked.length > 0 ? 'Also evaluated' : 'Evaluated'}
                </th>
              </tr>
              {rest.map((l) => (
                <LeverRow key={`rest-${l.leverType}-${l.label}`} lever={l} ranked={false} />
              ))}
            </tbody>
          ) : null}
        </table>
      </div>

      <p className="mechanic__footnote">
        A lever with no basis points was still tested — the status says whether there was nothing to
        capture, whether a validation limit applied, or whether the inputs to test it were missing.
      </p>
    </section>
  );
}
